/**
 * Sample FormGenerator question configs for the design-system playground.
 * Each sample is a single-question form (array of one question).
 */

const baseMeta = (id, questionType, overrides = {}) => ({
	id,
	step: 1,
	visible: true,
	questionType,
	fieldName: overrides.fieldName || questionType,
	question: overrides.question || questionType,
	errorMessage: overrides.errorMessage || 'Please complete this field',
	...overrides
});

export const generalSamples = [
	{
		title: 'Radio',
		description: 'Single-select lozenge options.',
		questions: [
			baseMeta(1, 'radio', {
				fieldName: 'membershipType',
				question: 'Which membership type?',
				options: [
					{ id: 'individual', label: 'Individual' },
					{ id: 'family', label: 'Family' }
				]
			})
		]
	},
	{
		title: 'Checkbox',
		description: 'Multi-select options.',
		questions: [
			baseMeta(1, 'checkbox', {
				fieldName: 'interests',
				question: 'What are you interested in?',
				options: [
					{ id: 'tennis', label: 'Tennis', value: 'tennis' },
					{ id: 'padel', label: 'Padel', value: 'padel' },
					{ id: 'pickleball', label: 'Pickleball', value: 'pickleball' }
				]
			})
		]
	},
	{
		title: 'Select',
		description: 'Dropdown select.',
		questions: [
			baseMeta(1, 'select', {
				fieldName: 'region',
				question: 'Which region are you based in?',
				placeholder: 'Select a region',
				options: [
					{ id: 'london', label: 'London' },
					{ id: 'south-east', label: 'South East' },
					{ id: 'midlands', label: 'Midlands' },
					{ id: 'north', label: 'North' }
				]
			})
		]
	},
	{
		title: 'Counter',
		description: 'Numeric stepper fields (`counter` → CounterComponent).',
		questions: [
			baseMeta(1, 'counter', {
				fieldName: 'participants',
				question: 'How many participants?',
				errorMessage: 'Please enter participant details',
				minimum: 0,
				max: 99999,
				incrementBy: 1,
				sections: [
					{
						title: 'Adults (18+)',
						options: [
							{ id: 'male', label: 'Male', value: 0 },
							{ id: 'female', label: 'Female', value: 0 }
						]
					},
					{
						title: 'Juniors (under 18)',
						options: [
							{ id: 'boys', label: 'Boys', value: 0 },
							{ id: 'girls', label: 'Girls', value: 0 }
						]
					}
				]
			})
		]
	},
	{
		title: 'Text area',
		description: 'Uses InputField (`textArea`) with `isTextArea` / textarea behaviour.',
		questions: [
			baseMeta(1, 'textArea', {
				fieldName: 'notes',
				question: 'Anything else we should know?',
				placeholder: 'Enter details',
				type: 'textarea',
				isTextArea: true,
				rows: 4
			})
		]
	},
	{
		title: 'Text input',
		description: 'Single-line InputField (`textInput`).',
		questions: [
			baseMeta(1, 'textInput', {
				fieldName: 'firstName',
				question: 'What is your first name?',
				label: 'First name',
				placeHolder: 'Enter first name',
				errorMessage: 'Please enter first name'
			})
		]
	},
	{
		title: 'ITT provider / free text',
		description:
			'`ittProviderSearch` → InputField. Used widely for free-text labels (company name, bank details, long answers).',
		questions: [
			baseMeta(1, 'ittProviderSearch', {
				fieldName: 'companyName',
				question: 'Name of the company you work for?',
				label: 'Company name',
				placeHolder: 'Enter company name',
				hasLimit: true,
				charLimit: 3,
				errorMessage: 'Please enter a company name'
			})
		]
	},
	{
		title: 'School sort code',
		description: 'Segmented code input (`schoolSortCode` → SchoolInputCode). Also used for bank sort codes.',
		questions: [
			baseMeta(1, 'schoolSortCode', {
				fieldName: 'sortCode',
				question: 'Bank account sort code',
				label: 'Bank account sort code',
				errorMessage: 'Please enter bank account sort code'
			})
		]
	},
	{
		title: 'Date',
		description: 'Date picker (`dateComponent`).',
		questions: [
			baseMeta(1, 'dateComponent', {
				fieldName: 'dateOfBirth',
				question: 'What is your date of birth?',
				label: 'Date of birth'
			})
		]
	},
	{
		title: 'Duet date',
		description: 'Accessible date input (`duetDateComponent`).',
		questions: [
			baseMeta(1, 'duetDateComponent', {
				fieldName: 'startDate',
				question: 'Preferred start date',
				label: 'Start date'
			})
		]
	},
	{
		title: 'Info',
		description: 'Read-only key points (`info`).',
		questions: [
			baseMeta(1, 'info', {
				fieldName: 'infoBlock',
				question: 'Before you continue',
				isOptional: true,
				subtitle: 'Prepare your details',
				instructionText: 'Have your venue contact information and programme details ready before submitting.'
			})
		]
	},
	{
		title: 'Contact number',
		description: 'Phone number field.',
		questions: [
			baseMeta(1, 'contactNumber', {
				fieldName: 'phoneNumber',
				question: 'What is your contact number?',
				label: 'Mobile number'
			})
		]
	},
	{
		title: 'Child contact number',
		description:
			'`childContactNumber` only renders when `sessionPlayerList` has an under-18 player selected. Includes both questions for a working demo.',
		questions: [
			baseMeta(1, 'sessionPlayerList', {
				fieldName: 'sessionplayers',
				question: 'Add players to sessions',
				instructionText: 'Select at least one under-18 player to reveal the emergency contact field.',
				errorMessage: 'Please select an option',
				supportingProps: {
					sessions: [
						{
							id: 121,
							sessionName: 'Juniors',
							availableSeats: 2,
							minAge: 8,
							maxAge: 15,
							ageLimitMessage: 'Ages 8-15',
							isFullyBooked: false,
							instructionText: 'This session is only suitable for children aged 8 - 15 years old',
							selectAllText: 'Select all players',
							feesText: 'Free',
							sessionDateText: 'Thursday, 10th February 2022',
							sesionStartTimeText: '11:00',
							sesionEndTimeText: '12:00',
							players: [
								{
									id: 'adult-1',
									sfid: '00325000021q04LAAQ',
									name: 'Sally Mann',
									gender: 'Female',
									dateOfBirth: '2000-11-13',
									ageText: '25 years old'
								},
								{
									id: 'child-1',
									sfid: '00325000021q04LBB',
									name: 'Beth Mann',
									gender: 'Female',
									dateOfBirth: '2015-11-13',
									ageText: '10 years old'
								}
							]
						}
					]
				}
			}),
			baseMeta(2, 'childContactNumber', {
				fieldName: 'emergencyContact',
				question: 'Review and confirm',
				subQuestion: 'What is your emergency contact details?',
				instructionText: 'In the case of an emergency, please provide an emergency contact for the added children.',
				label: 'Emergency contact number',
				type: 'tel',
				hasLimit: true,
				minLength: 11,
				errorMessage: 'Please enter a emergency contact number',
				showCountryCode: false,
				placeHolder: 'Enter emergency contact as 07XXXXXXXXX'
			})
		]
	},
	{
		title: 'File upload',
		description: 'File picker (`fileUpload`). `acceptedFileTypes` must be an array of MIME types.',
		questions: [
			baseMeta(1, 'fileUpload', {
				fieldName: 'certificate',
				question: 'Upload a certificate',
				label: 'Choose file',
				acceptedFileTypes: ['image/jpeg', 'image/png', 'application/pdf']
			})
		]
	},
	{
		title: 'Form table',
		description: '`formTable` → Table. Needs `apiUrl` for live course/learning data.',
		questions: [
			baseMeta(1, 'formTable', {
				fieldName: 'table',
				question: 'This was the learning that you completed',
				questionPrefix: 'Over the last 12 months',
				apiUrl: '/api/officials/mycpdplandoreview/coursedetails',
				isOptional: true
			})
		]
	},
	{
		title: 'Player list',
		description: '`playerList` — household / booking player picker with static supporting players.',
		questions: [
			baseMeta(1, 'playerList', {
				fieldName: 'yourDetails',
				question: 'Your details',
				questionCustomStyleClass: 'u-subtitle1',
				errorMessage: '',
				isOptional: true,
				supportingProps: {
					players: [
						{
							id: 1,
							sfid: '00325000021q04LAAQ',
							name: 'Nigel Mann',
							gender: 'Male',
							dateOfBirth: '',
							ageText: '34 years old'
						}
					],
					allowNewHouseholdMember: false
				}
			})
		]
	},
	{
		title: 'Session player list',
		description: '`sessionPlayerList` — assign players to sessions. Live session APIs optional for full booking flow.',
		questions: [
			baseMeta(1, 'sessionPlayerList', {
				fieldName: 'sessionplayers',
				question: 'Add players to sessions',
				instructionText: 'Only sessions suitable for each player will be displayed',
				errorMessage: 'Please select an option',
				supportingProps: {
					sessions: [
						{
							id: 121,
							sessionName: 'Juniors',
							availableSeats: 2,
							minAge: 8,
							maxAge: 15,
							ageLimitMessage: 'Ages 8-15',
							isFullyBooked: false,
							instructionText: 'This session is only suitable for children aged 8 - 15 years old',
							selectAllText: 'Select all players',
							feesText: 'Free',
							sessionDateText: 'Thursday, 10th February 2022',
							sesionStartTimeText: '11:00',
							sesionEndTimeText: '12:00',
							players: [
								{
									id: 'b2de831a-a730-48c4-b372-54aff1113d37',
									sfid: '00325000021q04LAAQ',
									name: 'Sally Mann',
									gender: 'Female',
									dateOfBirth: '2000-11-13',
									ageText: '25 years old'
								},
								{
									id: '84559f31-66b2-4656-93b1-ae770a3212a3',
									sfid: '00325000021q04LBB',
									name: 'Beth Mann',
									gender: 'Female',
									dateOfBirth: '2015-11-13',
									ageText: '10 years old'
								}
							]
						}
					]
				}
			})
		]
	},
	{
		title: 'Venue address search',
		description: 'Address lookup (`venueAddressSearch`). Needs address APIs for full flow.',
		questions: [
			baseMeta(1, 'venueAddressSearch', {
				fieldName: 'deliveryAddress',
				question: 'Your address',
				instructionText: 'Find your address or enter it manually.',
				AddressLabel: 'House No. and Street Name',
				manualAddressLabel: 'House No. and Street Name'
			})
		],
		formConfig: { saveAddress: false }
	},
	{
		title: 'Address finder',
		description: '`addressFinder` — search or enter address manually. Needs address APIs for full lookup.',
		questions: [
			baseMeta(1, 'addressFinder', {
				fieldName: 'address',
				question: 'What is your address?',
				placeHolder: 'Search for your address',
				errorMessage: 'Please enter your address'
			})
		]
	},
	{
		title: 'County search',
		description: 'Search input with county mapping.',
		questions: [
			baseMeta(1, 'countySearch', {
				fieldName: 'county',
				question: 'Which county?',
				placeHolder: 'Search for a county',
				errorMessage: 'Please select a county'
			})
		]
	},
	{
		title: 'Checkout',
		description:
			'`checkout` → CheckoutComponent. Needs payment / discount APIs and `userDetails` / checkout success handlers for full flow.',
		questions: [
			baseMeta(1, 'checkout', {
				fieldName: 'paymentId',
				question: 'Checkout',
				extraSubtitle: 'Please select a payment method you would like to use to order your kit.',
				agreementText:
					'By clicking “Continue”, you agree to the <a href="/terms-and-conditions" target="_blank">Terms and Conditions</a> and <a href="/privacy-policy" target="_blank">Privacy Policy</a>.',
				isDiscount: true,
				discountVocuherApi: '/api/validate/discount/voucher',
				contactId: '12345789',
				discountTimer: 180,
				discountError: {
					noEmpty: 'Please enter coupon code',
					duplicate: 'This coupon has already been applied',
					maxLimit: 'You have reached the maximum number of coupons allowed for this order',
					percentage: 'This coupon cannot be combined with other applied offers'
				}
			})
		],
		formConfig: {
			userDetails: {
				name: 'Test User',
				email: 'john.doe@example.com'
			}
		}
	}
];

export const householdSamples = [
	{
		title: 'Household member list',
		description:
			'Module type `householdMemberList`. Nested APIs live under `supportingProps` (member list fetch, add-member modal, review page). Live APIs needed for full behaviour.',
		questions: [
			baseMeta(1, 'householdMemberList', {
				fieldName: 'householdMembers',
				question: 'Who is in your household?',
				errorMessage: 'Please add at least one member',
				questionClass: 'u-subtitle1',
				supportingProps: {
					getHouseholdMemberDetails: {
						endpoint: '/api/youth-starter-offer/getHouseholdMemberDetails',
						method: 'GET'
					},
					getHouseholdMemberFormInit: {
						endpoint: '/api/AccountRegisterApi/getHouseholdMemberForm/Steps',
						method: 'GET',
						formPageImagePath: '/static/images/advantage.png',
						addToHouseholdButtonApi: {
							endpoint: '/api/householdAccounts/saveMember',
							method: 'POST'
						}
					},
					addToHouseholdReviewPageDetails: {
						pageTitle: 'Review household members',
						addMemberBtnText: 'Add another household member',
						submitBtnText: 'Finish setting up household',
						summaryPageImagePath: '/static/images/advantage.png',
						reviewHousholdButtonApi: {
							endpoint: '/api/householdAccounts/finalSubmit',
							method: 'POST'
						}
					},
					addHouseholdMemberSuccessPageDetails: {
						pageSubTitle: 'Congratulations!',
						pageTitle: 'Accounts added to household',
						successPageImagePath: '/static/images/advantage.png',
						actionButtonText: 'Return to order form'
					},
					addHouseholdMemberLinkText: 'Add a child',
					allowNewHouseholdMember: true
				}
			})
		],
		formConfig: { questionRenderersKey: 'household' }
	},
	{
		title: 'Household account onboarding',
		description:
			'Module type `householdAccountOnboarding`. Requires `subComponents.radio` (and usually email / registrationForm / inviteBlock) — matches AccountRegister add-member form.',
		questions: [
			baseMeta(1, 'householdAccountOnboarding', {
				fieldName: 'householdAccountSetup',
				question: 'Add a household member',
				questionCustomStyleClass: 'u-title-case1',
				errorMessage: 'Please select an option',
				subComponents: {
					radio: {
						type: 'radio',
						instructionText: 'Does the member already have an existing Advantage account?',
						fieldName: 'hasExistingAccount',
						errorMessage: 'Please select an option',
						options: [
							{ id: 'yes', label: 'Yes, they have an account', dependentQuestion: ['email'] },
							{
								id: 'no',
								label: "No, they don't have an account",
								dependentQuestion: ['registrationForm', 'inviteBlock']
							}
						]
					},
					email: {
						type: 'textInput',
						fieldName: 'emailAddress',
						instructionText: 'Find and add members to your household by searching their account details.',
						instructionTextStyleClss: 'u-subtitle3',
						label: 'Email address',
						placeholder: 'Enter email address',
						buttonText: 'Find member(s)',
						validation: {
							required: true,
							errorMessage: 'Please enter a email address',
							invalidErrorMessage: 'Please enter a valid email address'
						},
						api: {
							url: '/api/householdAccounts/findMembers'
						}
					},
					registrationForm: {
						firstName: {
							type: 'textInput',
							fieldName: 'firstName',
							instructionText: "Please provide the personal details for the child you're creating for.",
							instructionTextStyleClss: 'u-subtitle3',
							label: 'First name',
							placeholder: 'Enter first name',
							validation: {
								required: true,
								errorMessage: 'Please enter first name',
								allowNumbersOrSpecialCharacters: false,
								invalidErrorMessage: 'Please enter a valid first name',
								hasLimit: true,
								charLimit: 2,
								minLengthErrorMessage: 'First name must be 2 or more letters'
							}
						},
						lastName: {
							type: 'textInput',
							fieldName: 'lastName',
							label: 'Last name',
							placeholder: 'Enter last name',
							validation: {
								required: true,
								errorMessage: 'Please enter last name',
								allowNumbersOrSpecialCharacters: false,
								invalidErrorMessage: 'Please enter a valid last name',
								hasLimit: true,
								charLimit: 2,
								minLengthErrorMessage: 'Last name must be 2 or more letters'
							}
						},
						dateOfBirth: {
							type: 'dateComponent',
							fieldName: 'dateOfBirth',
							label: 'Date of birth',
							errorMessage: 'Please enter a valid date',
							validation: { required: true },
							minPastYears: {
								amount: 4,
								errorMessage: 'Members need to be over 4 years of age'
							},
							maxPastYears: {
								amount: 18,
								errorMessage:
									"You can't create an account for anyone over 18. Once they've created their own account, you can link them in your account settings."
							},
							futureDateErrorMessage: 'Please enter a date in the past',
							instructionTextStyleClss: 'u-body2',
							isOptional: false
						},
						gender: {
							type: 'radio',
							fieldName: 'gender',
							instructionText: 'Gender',
							errorMessage: 'Please select a gender before proceeding',
							options: [
								{ id: 'male', label: 'Male' },
								{ id: 'female', label: 'Female' },
								{ id: 'nonBinary', label: 'Non-binary' },
								{ id: 'preferNot', label: 'Prefer not to say' }
							]
						},
						emailInfoBlock: {
							type: 'info',
							fieldName: 'info',
							wrapperClasses: 'u-mb-2',
							subtitle: 'Advantage registration',
							subtitleStyleClass: 'u-subtitle3 u-mb-2',
							instructionText:
								'We noticed this user is over 13. Enter their email address and they will receive an email with instructions on how to create their own login.',
							instructionTextStyleClass: 'u-body1',
							questionType: 'info',
							visible: true,
							isOptional: true,
							dependentQuestion: [],
							customStylingClass: true,
							hasSeperator: true
						},
						email: {
							type: 'textInput',
							fieldName: 'childEmail',
							label: 'Email address',
							placeholder: 'Enter email address',
							validation: {
								required: true,
								errorMessage: 'Please enter a email address',
								invalidErrorMessage: 'Please enter a valid email address'
							},
							api: {
								url: '/api/AccountRegisterApi/CheckIfEmailExists',
								apiFailError: 'Something went wrong please try again',
								duplicateEmailError: 'This email is already registered. Try another email address'
							}
						}
					},
					infoBlock: {
						type: 'info',
						fieldName: 'info',
						wrapperClasses: 'u-my-3',
						instructionText:
							'By adding this individual(s) to my household I confirm that I understand and agree to the Terms and Conditions.',
						questionType: 'info',
						visible: true,
						isOptional: true,
						dependentQuestion: [],
						customStylingClass: true
					},
					inviteBlock: {
						text: 'Invite a new member',
						description:
							'All users over 18 must have an Advantage account to be added to a household. Once they have created an account, you can link them to your household.',
						socialButtons: [
							{
								icon: 'email-circle',
								link: 'mailto:?subject=Referral Link for Onboarding to Play&body=You have received referral%0Ahttps://www.lta.org.uk/advantage/'
							},
							{
								icon: 'whatsapp-circle',
								link: 'https://web.whatsapp.com/send?text=You have received referral%0Ahttps://www.lta.org.uk/advantage/'
							}
						]
					}
				}
			})
		],
		formConfig: { questionRenderersKey: 'household' }
	}
];

export const searchFinderSamples = [
	{
		title: 'School search',
		description: '`schoolSearch` via SchoolFinder.',
		questions: [
			baseMeta(1, 'schoolSearch', {
				fieldName: 'school',
				question: 'Find your school',
				placeHolder: 'Search for a school',
				errorMessage: 'Please select a school'
			})
		]
	},
	{
		title: 'University search',
		description: '`universitySearch` via SchoolFinder (ITT university defaults).',
		questions: [
			baseMeta(1, 'universitySearch', {
				fieldName: 'university',
				question: 'Name of your University?',
				instructionText: 'Search for your university by using its name.',
				label: 'University finder',
				placeHolder: 'Type the University Name',
				errorMessage: 'Please select a university'
			})
		]
	},
	{
		title: 'Nursery search',
		description: '`nurserySearch` via SchoolFinder.',
		questions: [
			baseMeta(1, 'nurserySearch', {
				fieldName: 'nurseryAddress',
				question: 'Find your nursery',
				label: 'Nursery',
				instructionText: 'Search for your nursery using the finder below.',
				manualAddressLabel: "Can't find your nursery? Enter manually",
				placeHolder: 'e.g. Balham Nursery',
				errorMessage: 'Please select a nursery'
			})
		]
	},
	{
		title: 'College search',
		description: '`collegeSearch` via SchoolFinder.',
		questions: [
			baseMeta(1, 'collegeSearch', {
				fieldName: 'collegeAddress',
				question: 'Find your college',
				label: 'College',
				instructionText: 'Search for your college using the finder below.',
				manualAddressLabel: "Can't find your college? Enter manually",
				placeHolder: 'e.g. Balham',
				errorMessage: 'Please select a college'
			})
		]
	},
	{
		title: 'Coach search',
		description: '`coachSearch` via CoachFinder.',
		questions: [
			baseMeta(1, 'coachSearch', {
				fieldName: 'coach',
				question: 'Find a coach',
				placeHolder: 'Search for a coach',
				errorMessage: 'Please select a coach'
			})
		]
	},
	{
		title: 'Park finder',
		description: '`parkFinder`.',
		questions: [
			baseMeta(1, 'parkFinder', {
				fieldName: 'park',
				question: 'Find a park',
				placeHolder: 'Search for a park',
				errorMessage: 'Please select a park'
			})
		]
	},
	{
		title: 'Coach venue search',
		description: '`coachVenueSearch` (ParkFinder + coach venue defaults).',
		questions: [
			baseMeta(1, 'coachVenueSearch', {
				fieldName: 'venue',
				question: 'Find your venue',
				placeHolder: 'Search for a venue',
				errorMessage: 'Please select a venue'
			})
		]
	},
	{
		title: 'Activator venue search',
		description: '`activatorVenueSearch`.',
		questions: [
			baseMeta(1, 'activatorVenueSearch', {
				fieldName: 'venue',
				question: 'Find an activator venue',
				placeHolder: 'Search for a venue',
				errorMessage: 'Please select a venue'
			})
		]
	},
	{
		title: 'Tournament search',
		description: '`tournamentSearch`.',
		questions: [
			baseMeta(1, 'tournamentSearch', {
				fieldName: 'tournament',
				question: 'Find a tournament',
				placeHolder: 'Search for a tournament',
				errorMessage: 'Please select a tournament'
			})
		]
	},
	{
		title: 'Tournament event details',
		description: 'Read-only summary (`tournamentEventDetails`) of event day counts.',
		questions: [
			baseMeta(1, 'tournamentEventDetails', {
				fieldName: 'eventDetails',
				question: 'Event details',
				isOptional: true,
				value: {
					miniDays: '1',
					juniorDays: '2',
					openDays: '1',
					seniorDays: '2',
					noOfEvents: '1',
					noOfEntries: '2'
				}
			})
		]
	},
	{
		title: 'Tournament event form',
		description:
			'`tournamentEventForm` — editable event day / entry fields. Includes `tournamentEventFormElement` labels required by the component.',
		questions: [
			baseMeta(1, 'tournamentEventForm', {
				fieldName: 'eventForm',
				question: 'Event details',
				tournamentEventFormElement: {
					miniDays: {
						id: 'mini-days',
						label: 'Mini days',
						errorMessage: 'Please enter mini days',
						type: 'number'
					},
					juniorDays: {
						id: 'junior-days',
						label: 'Junior days',
						errorMessage: 'Please enter junior days',
						type: 'number'
					},
					openDays: {
						id: 'open-days',
						label: 'Open days',
						errorMessage: 'Please enter open days',
						type: 'number'
					},
					seniorDays: {
						id: 'senior-days',
						label: 'Senior days',
						errorMessage: 'Please enter senior days',
						type: 'number'
					},
					noOfEvents: {
						id: 'no-of-events',
						label: 'Number of events',
						errorMessage: 'Please enter number of events',
						type: 'number'
					},
					noOfEntries: {
						id: 'no-of-entries',
						label: 'Number of entries',
						errorMessage: 'Please enter number of entries',
						type: 'number'
					},
					noOfDaysWorked: {
						id: 'no-of-days-worked',
						label: 'Number of days worked',
						errorMessage: 'Please enter number of days worked',
						type: 'number'
					}
				}
			})
		]
	},
	{
		title: 'Mentor finder',
		description: '`mentorFinder`.',
		questions: [
			baseMeta(1, 'mentorFinder', {
				fieldName: 'mentor',
				question: 'Find a mentor',
				placeHolder: 'Search for a mentor',
				errorMessage: 'Please select a mentor'
			})
		]
	},
	{
		title: 'Authority finder',
		description: '`auhorityFinder` (note: legacy typo in QuestionTypes).',
		questions: [
			baseMeta(1, 'auhorityFinder', {
				fieldName: 'authority',
				question: 'Find a local authority',
				placeHolder: 'Search for an authority',
				errorMessage: 'Please select an authority'
			})
		]
	},
	{
		title: 'Affiliated venue search list',
		description: '`affiliateVenueSearchList`.',
		questions: [
			baseMeta(1, 'affiliateVenueSearchList', {
				fieldName: 'venues',
				question: 'Your affiliated venues',
				myAffiliatedVenues: [
					{
						id: 'example-venue',
						venue: 'Example Tennis Club',
						address: { postCode: 'SW1A 1AA' }
					}
				]
			})
		]
	},
	{
		title: 'Tennis leader primary venue',
		description: '`tennisLeaderPrimaryVenueSearch`.',
		questions: [
			baseMeta(1, 'tennisLeaderPrimaryVenueSearch', {
				fieldName: 'primaryVenue',
				question: 'Select your primary venue',
				placeHolder: 'Search for a venue',
				errorMessage: 'Please select a venue'
			})
		]
	}
];

export const venueRegistrationSamples = [
	{
		title: 'Venue selection (empty)',
		description:
			'`venueSelection` with no venues — empty-state heading and "Search for your venue" CTA open the nested search modal.',
		questions: [
			baseMeta(1, 'venueSelection', {
				fieldName: 'venueId',
				question: 'Which organisation would you like to complete your registration for?',
				singleVenueQuestion: 'You are completing your registration for this organisation',
				emptyVenueQuestion: 'Please search for the organisation you want to register',
				errorMessage: 'Please select your venue',
				answerField: 'id',
				valueField: 'value',
				isSkipButtonVisible: true,
				skipButtonTitle: 'Search for your venue',
				venues: []
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration',
			radioAnswerField: 'id',
			showExitWithoutSavingButton: true,
			exitWithoutSavingButtonLabel: 'Exit without saving',
			exitWithoutSavingButtonClasses: 'lta-btn--secondary',
			nextStepButtonLabel: 'Save and continue',
			submitButton: {
				title: 'Save and continue',
				alwaysVisible: true
			}
		}
	},
	{
		title: 'Venue selection (multiple)',
		description:
			'Module type `venueSelection`. Multiple venues render as radios + cards; "Venue not showing?" opens nested search modal (`#js-venue-selection-search-modal`).',
		questions: [
			baseMeta(1, 'venueSelection', {
				fieldName: 'venueId',
				question: 'Which organisation would you like to complete your registration for?',
				singleVenueQuestion: 'You are completing your registration for this organisation',
				errorMessage: 'Please select your venue',
				answerField: 'id',
				valueField: 'value',
				isSkipButtonVisible: true,
				skipButtonTitle: 'Venue not showing? Search here',
				venues: [
					{
						id: 'spencer-lawn-tennis',
						venue: 'Organisation Name',
						address: { postCode: 'Address' }
					},
					{
						id: 'riverside-tennis-club',
						venue: 'Organisation Name',
						address: { postCode: 'Address' }
					}
				]
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration',
			radioAnswerField: 'id',
			showExitWithoutSavingButton: true,
			exitWithoutSavingButtonLabel: 'Exit without saving',
			exitWithoutSavingButtonClasses: 'lta-btn--secondary',
			nextStepButtonLabel: 'Save and continue',
			submitButton: {
				title: 'Save and continue',
				alwaysVisible: true
			}
		}
	},
	{
		title: 'Venue selection (single)',
		description: '`venueSelection` with one venue — card only, auto-selected (no radio).',
		questions: [
			baseMeta(1, 'venueSelection', {
				fieldName: 'venueId',
				question: 'Which organisation would you like to complete your registration for?',
				singleVenueQuestion: 'You are completing your registration for this organisation',
				errorMessage: 'Please select your venue',
				answerField: 'id',
				valueField: 'value',
				isSkipButtonVisible: true,
				skipButtonTitle: 'Venue not showing? Search here',
				venues: [
					{
						id: 'spencer-lawn-tennis',
						venue: 'Spencer Lawn Tennis',
						address: { postCode: 'Address' }
					}
				]
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration',
			radioAnswerField: 'id',
			showExitWithoutSavingButton: true,
			exitWithoutSavingButtonLabel: 'Exit without saving',
			exitWithoutSavingButtonClasses: 'lta-btn--secondary',
			nextStepButtonLabel: 'Save and continue',
			submitButton: {
				title: 'Save and continue',
				alwaysVisible: true
			}
		}
	},
	{
		title: 'Organisation address search',
		description:
			'Module type `organisationAddressSearch` → VenueAddressSearch. Needs address APIs for full lookup flow.',
		questions: [
			baseMeta(1, 'organisationAddressSearch', {
				fieldName: 'organisationAddress',
				question: '',
				instructionText: 'This is the address of where your organisation is located.',
				errorMessage: 'Please enter your organisation address',
				AddressLabel: 'House no. and street name',
				manualAddressLabel: 'House no. and street name',
				hasLimit: false,
				charLimit: 0,
				minSearchChar: 0
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration',
			saveAddress: false
		}
	},
	{
		title: 'Delivery address same as organisation',
		description:
			'Module type `checkbox` with venue-registration spacing (`hasSelectAll: false`, optional single option).',
		questions: [
			baseMeta(1, 'checkbox', {
				fieldName: 'deliveryAddressSameAsOrganisation',
				question: '',
				errorMessage: 'Please select an option',
				isOptional: true,
				hasSelectAll: false,
				minSelectable: 0,
				options: [
					{
						id: 'same',
						label: 'The organisation delivery address is the same as the organisation address',
						dependentQuestion: []
					}
				]
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration'
		}
	},
	{
		title: 'Delivery address search',
		description:
			'Module type `deliveryAddressSearch` → VenueAddressSearch. Needs address APIs for full lookup flow.',
		questions: [
			baseMeta(1, 'deliveryAddressSearch', {
				fieldName: 'deliveryAddress',
				question: '',
				errorMessage: 'Please enter your delivery address',
				AddressLabel: 'House no. and street name',
				manualAddressLabel: 'House no. and street name',
				hasLimit: false,
				charLimit: 0,
				minSearchChar: 0
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration',
			saveAddress: false
		}
	},
	{
		title: 'Venue registration venue search',
		description:
			'Module type `venueRegistrationVenueSearch` → VenueRegistrationVenueFinder. Needs venue search APIs for full flow.',
		questions: [
			baseMeta(1, 'venueRegistrationVenueSearch', {
				fieldName: 'venueSearch',
				question: 'Search for your organisation',
				placeHolder: 'Search for a venue',
				errorMessage: 'Please select a venue'
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration'
		}
	},
	{
		title: 'Payment options',
		description: 'Module type `paymentOptions` — exclusive “Always free” option with `hasOrSeparator`.',
		questions: [
			baseMeta(1, 'paymentOptions', {
				fieldName: 'venuePaymentOptions',
				question: 'Provide the payment options available at your venue',
				errorMessage: 'Please select at least one option',
				hasSelectAll: false,
				options: [
					{
						id: 'membership',
						label: 'Membership',
						value: 'Membership',
						dependentQuestion: []
					},
					{
						id: 'pay_and_play',
						label: 'Pay and Play',
						value: 'Pay and Play',
						dependentQuestion: []
					},
					{
						id: 'sometimes_free',
						label: 'Sometimes free',
						value: 'Sometimes free',
						dependentQuestion: []
					},
					{
						id: 'always_free',
						label: 'Always free',
						value: 'Always free',
						hasOrSeparator: true,
						isExclusive: true,
						dependentQuestion: []
					}
				]
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration'
		}
	},
	{
		title: 'Add new contact',
		description:
			'Module type `addNewContact` (name search). Needs team member search APIs for full lookup.',
		questions: [
			baseMeta(1, 'addNewContact', {
				fieldName: 'memberSearch',
				searchType: 'name',
				api: {
					url: '/api/venueRegistration/teamInformation/members/search'
				},
				label: 'Members name',
				description: 'This individual must be a member at your venue.',
				errorMessage: 'Please select a member',
				question: 'Add a new contact',
				extraSubtitle:
					'You can search via name for any contacts at your venue, or you can use their LTA membership number or Coach Code for a more specific search.'
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration'
		}
	},
	{
		title: 'Selected contact summary',
		description:
			'Module type `selectedContactSummary`. Pass `contactData` (or wire `getSelectedContact` in formConfig) to preview the card.',
		questions: [
			baseMeta(1, 'selectedContactSummary', {
				fieldName: 'selectedContactSummary',
				question: 'Selected contact',
				titlePattern: 'small',
				isOptional: true,
				contactData: {
					displayName: 'Jane Smith',
					membershipNumber: '12345678'
				}
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration'
		}
	},
	{
		title: 'Manual contact card',
		description:
			'Module type `manualContactCard` — manually enter Free Park Tennis Administrator details.',
		questions: [
			baseMeta(1, 'manualContactCard', {
				fieldName: 'contact',
				question: 'Add your Free Park Tennis Administrator',
				extraSubtitle:
					'The free park tennis administrator will be sent email notifications when an Activator specifies your park as a venue they wish to volunteer and deliver free park tennis sessions at.',
				errorMessage: 'Please save your contact details before continuing',
				fullNameLabel: 'Full name',
				emailLabel: 'Email address',
				fullNameErrorMessage: 'Please enter a full name',
				emailErrorMessage: 'Please enter a valid email address',
				saveButtonLabel: 'Save contact',
				removeButtonLabel: 'Remove as FPT Admin'
			})
		],
		formConfig: {
			questionRenderersKey: 'venueRegistration'
		}
	}
];

export const samplesBySection = {
	general: generalSamples,
	household: householdSamples,
	'search-finders': searchFinderSamples,
	'venue-registration': venueRegistrationSamples
};
